import queryString from 'query-string'
import React, { UIEvent, useCallback, useEffect, useRef, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { useToastState } from '~/atoms/ToastState'
import { Text } from '~/components/core'
import { FontStyle } from '~/components/core/Text'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { FILESIZE, MMSTypes } from '~/constants/config'
import { PendingMessage } from '~/pages/Messaging/hooks/usePendingMessages'
import { newUuid } from '~/utils/common'
import { fileToBase64 } from '~/utils/formatter'
import { useThreadMessagesViewModel } from '~/viewModels/Messaging/ThreadMessagesViewModel'

import { Message, MessageThread, PaginatedApiResult } from '@mangovoice/types'

import { isEnterForAnotherControl } from './composerKeys'
import MessageInput from './MessageInput'
import MessageList from './MessageList'
import {
  ChatDragContentWrapper,
  ChatOverlay,
  ChatRootWrapper,
} from './MessagingStyles'

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`

interface ThreadProps {
  currentThread: MessageThread
}

const ThreadLoader = () => {
  return (
    <LoadingWrapper>
      <LoadingSpinner />
    </LoadingWrapper>
  )
}

const Thread = ({ currentThread }: ThreadProps) => {
  const { t } = useTranslation()
  const messageEnd = useRef<HTMLInputElement>(null)
  const inputBoxRef = useRef<HTMLTextAreaElement>(null)

  const [visibleThreadMessages, setVisibleThreadMessages] = useState<
    Message[] | null
  >(null)

  const {
    messagingToast: [, setMessagingToastState],
  } = useToastState()
  const [msgFile, setMsgFile] = useState<File | undefined>()
  const [fileUrl, setFileUrl] = useState<string>()
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const {
    messagingModal: [messagingModalState],
  } = useModalState()

  const fetchMessageLimit = 25
  const contactName = currentThread.name || currentThread.external_phone_number

  const updateVisibleThreadMessages = (data: PaginatedApiResult<Message>) => {
    const { count, next, results } = data
    const { offset } = queryString.parse(next ?? '', {
      parseNumbers: true,
    }) as { offset: number | null | undefined }
    const start = offset ? offset - results.length : count - results.length
    let res = (visibleThreadMessages ?? []).slice(0, start)
    res = [...res, ...results]

    setVisibleThreadMessages(res)
  }

  const handleThreadMessagesUpdated = (data: PaginatedApiResult<Message>) => {
    updateVisibleThreadMessages(data)
  }

  const {
    pendingMessages,
    onThreadMessagesUpdated,
    getThreadMessages,
    tryGetNextPage,
    isThreadFetching,
    sendThreadMessage,
    messageText,
    setMessageText,
  } = useThreadMessagesViewModel(currentThread.id, contactName)

  onThreadMessagesUpdated(handleThreadMessagesUpdated)

  useEffect(() => {
    getThreadMessages({ offset: 0, limit: fetchMessageLimit })
  }, [currentThread])

  const scrollToBottom = () => {
    if (messageEnd.current) {
      messageEnd.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  const focusInput = () => {
    if (inputBoxRef.current) {
      inputBoxRef.current.focus()
    }
  }

  const handleClearInput = () => {
    setMessageText('')
    setFileUrl(undefined)
    setMsgFile(undefined)
  }

  useEffect(() => {
    scrollToBottom()
    focusInput()
  }, [])

  const onDrop: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ) => Promise<void> = useCallback(async (acceptedFiles, fileRejections) => {
    if (acceptedFiles[0]) {
      const dataURL = (await fileToBase64(acceptedFiles[0])) as string
      setMsgFile(acceptedFiles[0])
      setFileUrl(dataURL)
    }
    if (fileRejections[0]) {
      const errorCode = fileRejections[0].errors[0].code

      setMessagingToastState((currentMessagingState) => {
        return {
          ...currentMessagingState,
          messagingStatus: errorCode,
        }
      })
    }
    setIsOpenDialog(false)
  }, [])

  const onFileDialogCancel = () => {
    setIsOpenDialog(false)
  }

  const isDraggable = !!currentThread.id && !msgFile
  const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
    accept: MMSTypes,
    disabled: !isDraggable,
    maxSize: FILESIZE.MAX_MMS,
    noDrag: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
    onFileDialogCancel,
  })

  const handleMessageScroll = (e: UIEvent<HTMLDivElement>) => {
    const scrollHeight =
      e.currentTarget.scrollHeight - e.currentTarget.clientHeight
    if (scrollHeight + e.currentTarget.scrollTop < 2) {
      tryGetNextPage()
    }
  }

  const handleChangeMessage = (text: string | undefined) => {
    setMessageText(text)
  }

  const handleOpenAttach = () => {
    open()
    setIsOpenDialog(true)
  }

  const handleCloseInputImage = () => {
    setFileUrl(undefined)
    setMsgFile(undefined)
    setMessagingToastState({})
  }

  const handleSelectEmoji = (emoji: { native: string }) => {
    const text = `${messageText ?? ''}${emoji.native}`
    setMessageText(text)
    inputBoxRef.current?.focus()
  }

  const handleSendMessage = () => {
    if (!msgFile && messageText === '') return

    sendThreadMessage({
      tempUuid: newUuid(),
      body: messageText,
      file: msgFile,
    })
    handleClearInput()
  }

  const handleRetrySend = (message: PendingMessage) => {
    if (!message.originalSendParams) {
      return
    }
    sendThreadMessage(message.originalSendParams)
  }

  const handleKeydown = (e: KeyboardEvent) => {
    //don't accept input if the messaging modal is open
    if (messagingModalState.open) {
      return
    }
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !isEnterForAnotherControl(e.target, inputBoxRef.current)
    ) {
      e.preventDefault()
      handleSendMessage()
    }

    const isMac = navigator.platform.indexOf('Mac') > -1
    if (isMac) {
      if (e.metaKey && e.keyCode === 65) {
        inputBoxRef.current?.select()
      }
    } else if (e.ctrlKey && e.keyCode === 65) {
      inputBoxRef.current?.select()
    }
  }

  const disabled = messageText === '' && !fileUrl

  //disabled rule - this is intended to be a no-op

  const defaultInputCallback = useCallback((_?: any) => {}, [])
  const getToggledMessageInputProps = () => {
    const { open: messagingModalOpen } = messagingModalState

    return {
      onChange: !messagingModalOpen
        ? handleChangeMessage
        : defaultInputCallback,
      onKeyDown: !messagingModalOpen ? handleKeydown : defaultInputCallback,
      onOpen: !messagingModalOpen ? handleOpenAttach : defaultInputCallback,
      onClose: !messagingModalOpen
        ? handleCloseInputImage
        : defaultInputCallback,
      onSelect: !messagingModalOpen ? handleSelectEmoji : defaultInputCallback,
      onSend: !messagingModalOpen ? handleSendMessage : defaultInputCallback,
      onRemove: !messagingModalOpen ? handleClearInput : defaultInputCallback,
      snippetsDisabled: messagingModalOpen,
    }
  }

  const messages: (Message | PendingMessage)[] = [
    ...pendingMessages,
    ...(visibleThreadMessages ?? []),
  ]

  const shouldShowLoader =
    isThreadFetching && (visibleThreadMessages?.length ?? 0) <= 0

  return shouldShowLoader ? (
    <ThreadLoader />
  ) : (
    <ChatRootWrapper {...getRootProps()}>
      <input {...getInputProps()} />
      {isDragAccept && (
        <ChatOverlay>
          <ChatDragContentWrapper>
            <Text
              fontStyle={FontStyle.Large}
              text={t('messagingThread.dropFileToSend', 'Drop file to send')}
            />
          </ChatDragContentWrapper>
        </ChatOverlay>
      )}
      <MessageList
        ref={messageEnd}
        messages={messages}
        contactName={contactName}
        onScroll={handleMessageScroll}
        onRetrySend={handleRetrySend}
      />
      <MessageInput
        ref={inputBoxRef}
        ariaLabel={t('a11y.composer.labelWithName', { name: contactName })}
        file={msgFile}
        fileURL={fileUrl}
        opened={isOpenDialog}
        disabled={disabled}
        text={messageText}
        {...getToggledMessageInputProps()}
      />
    </ChatRootWrapper>
  )
}

export default Thread
